import { useFormContext } from "react-hook-form";

import Box from "@/components/Box";
import Divider from "@/components/Divider";
import { ExternalTextLink } from "@/components/Links";
import Loading from "@/components/Loading";
import {
  DOCUMENT_FILE_ACCEPT,
  DOCUMENT_FILE_ACCEPT_HELP_TEXT,
  FileInputWrapper,
  UPLOADING_STATUS,
  useUploadFileToS3
} from "@/components/UploadFileToS3";
import { DOCUMENT_COVERING_LETTER, DOCUMENT_STUDENT_ID } from "@/core/constants";
import FileIcon from "@/images/file-icon.svg?react";
import ControlledFormField from "@/tpo/ControlledFormField";
import ControlledRadioGroup from "@/tpo/ControlledRadioGroup";
import FloatingLabelInput from "@/tpo/FloatingLabelInput";
import Group from "@/tpo/Group";
import { Error } from "@/tpo/InputWrapper";
import Stack from "@/tpo/Stack";
import { get } from "@/utils/nodash";
import ButtonV2 from "@/v2/Buttons";

import { LevelFeatures as LevelFeaturesUi, LEVELS } from "../Levels";
import { PatientOrderPermission } from "../PatientOrderPermission";

export function DocumentProof({ label, name, disabled }) {
  const { setValue, watch } = useFormContext();
  const documents = watch("documents");

  const { status, upload, errorMessage } = useUploadFileToS3({
    accept: DOCUMENT_FILE_ACCEPT,
    onChange: ({ key, file }) =>
      setValue("documents", [
        ...documents,
        { key, name: file.name, type: name, qualification_type: "" }
      ])
  });

  const document = documents.find(d => d.type === name);

  return (
    <Stack gap={10}>
      <Box fontFamily="gilroyBold" fontSize={16}>
        {label}
      </Box>
      <Group
        alignItems="center"
        justifyContent="space-between"
        gap={20}
        flexWrap="wrap"
        maxWidth="100%"
      >
        <Group
          gap={20}
          style={{
            textOverflow: "ellipsis",
            overflow: "hidden"
          }}
          alignItems="center"
        >
          {status === UPLOADING_STATUS ? (
            <Loading minHeight={40} minWidth={40} size={20} />
          ) : (
            <FileIcon />
          )}
          <Box
            fontFamily="gilroyBold"
            fontSize={12}
            letterSpacing="2.8px"
            textTransform="uppercase"
          >
            {`${document?.name || "Add a file"}`}
          </Box>
        </Group>
        {!document ? (
          <FileInputWrapper>
            <input
              type="file"
              accept={DOCUMENT_FILE_ACCEPT}
              onChange={upload}
              disabled={status === UPLOADING_STATUS}
            />
            <ButtonV2
              color="green"
              type="button"
              data-component-name={`addQualificationFileButton:${name}`}
              size="sm"
              disabled={disabled}
            >
              upload
            </ButtonV2>
          </FileInputWrapper>
        ) : (
          <ButtonV2
            color="red"
            size="sm"
            onClick={() =>
              setValue("documents", [...documents.filter(doc => doc.key !== document.key)])
            }
            data-component-name={`removeQualificationFileButton:${name}`}
          >
            remove
          </ButtonV2>
        )}
      </Group>
      <Box fontFamily="gilroyMedium" fontSize={[12, 14, 14]} color="#818181" lineHeight="20px">
        {DOCUMENT_FILE_ACCEPT_HELP_TEXT}
      </Box>
      {errorMessage && (
        <Box fontFamily="gilroyMedium" fontSize={12} color="red" lineHeight="20px">
          {errorMessage}
        </Box>
      )}
    </Stack>
  );
}

export function LevelFeatures({ variant }) {
  const { watch } = useFormContext();
  const level = watch("level");

  if (!level || level === "Full access") return null;

  return (
    <>
      <Box>
        <Box fontFamily="gilroyBold" fontSize={[28]} mb={20}>
          {LEVELS[level].label} features
        </Box>
        <Box fontFamily="gilroyMedium" fontSize={16} lineHeight="24px" mb={40}>
          This level will give you access and permissions to the following services in the
          application.
        </Box>
        <Stack gap={40}>
          <LevelFeaturesUi level={level} />
        </Stack>
      </Box>
      {variant !== "upgrade" && <PatientOrderPermission />}
      <Divider />
    </>
  );
}

export default function EducationalInstitution() {
  const {
    formState: { errors }
  } = useFormContext();

  const documentError = get(errors, "documents")?.message;

  return (
    <Stack bg="white" pt={40} pb={80} px={[20, 20, 80]} gap={40} borderRadius={5}>
      <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]}>
        Educational institution
      </Box>
      <Stack gap={40}>
        <Stack>
          <Stack gap={20}>
            <ControlledFormField
              name="institutionName"
              label="Name of Institution"
              Component={FloatingLabelInput}
            />
            <ControlledFormField
              name="courseName"
              label="Course name"
              Component={FloatingLabelInput}
            />
          </Stack>
          <Box fontFamily="gilroyBold" lineHeight="28px" mt={40}>
            Current study level
          </Box>
          <Box fontFamily="gilroyMedium" lineHeight="24px" fontSize="16px" mb={40}>
            When completing your registration, please state your current{" "}
            <Box as="span" fontFamily="gilroyBold">
              qualification Level
            </Box>{" "}
            (e.g., Level 4, 5, or 6), not your year of study. Course years and levels do not always
            match. For example, you may be in{" "}
            <Box as="span" fontFamily="gilroyBold">
              Year 1 of a two-year Level 6 course
            </Box>{" "}
            and therefore studying at{" "}
            <Box as="span" fontFamily="gilroyBold">
              Level 5
            </Box>
            , or in{" "}
            <Box as="span" fontFamily="gilroyBold">
              Year 1 of a four-year Level 6 course
            </Box>{" "}
            and therefore studying at{" "}
            <Box as="span" fontFamily="gilroyBold">
              Level 4
            </Box>
            .
            <br />
            <br />
            You must provide your current Level as defined in your course documentation or the
            official UK qualification levels{" "}
            <ExternalTextLink href="https://www.gov.uk/what-different-qualification-levels-mean/list-of-qualification-levels">
              here
            </ExternalTextLink>
            .
          </Box>
          <ControlledRadioGroup
            Container={Stack}
            containerProps={{
              gap: 20
            }}
            name="level"
            label="Level"
            labelProps={{
              fontFamily: "gilroyBold",
              fontSize: 18,
              mb: 20
            }}
            values={[
              {
                label: "Level 4",
                value: "Level 4"
              },
              {
                label: "Level 5",
                value: "Level 5"
              },
              {
                label: "Level 6",
                value: "Level 6"
              }
            ]}
          />
        </Stack>
        <Divider />
        <LevelFeatures />
        <Stack gap={40}>
          <Stack gap={20}>
            <Box fontFamily="gilroyBold" fontSize={[28]}>
              Proof of Enrollment
            </Box>
            <Box>
              Please upload a valid proof of enrolment – student ID AND confirmation letter of
              current level from your colleges. We will require this before we can complete your
              approval to the platform.
            </Box>
          </Stack>
          <DocumentProof label="Student ID" name={DOCUMENT_STUDENT_ID} />
          <DocumentProof label="Confirmation letter" name={DOCUMENT_COVERING_LETTER} />
          {documentError && <Error error={documentError} />}
        </Stack>
      </Stack>
    </Stack>
  );
}
